import { allocateBoundedBudgetUnits } from "../budget-allocation";
import type { CampaignCreationCapabilities } from "../schemas/campaign-creation";
import {
  SMART_CREATION_MAX_CAMPAIGNS,
  type SmartCreationAdGroupDraftContentV2,
} from "../schemas/smart-creation";
import type {
  SmartCreationReviewAdGroup,
  SmartCreationReviewCampaign,
  SmartCreationReviewCampaignSeed,
  SmartCreationReviewWorkspace,
} from "../schemas/smart-creation-review";
import { updateDraftContentFromGraph } from "./draft-projection";
import {
  reviewTargetMissing,
  SmartCreationReviewError,
  type SmartCreationReviewErrorCode,
} from "./errors";
import { requireCampaign, userValue } from "./graph";
import { hash } from "./hashing";
import { targetingLane, targetStableKey } from "./lineage";
import { reviewTargetIntent, strategySupportsReviewIntent } from "./strategy-intent";
import type { AddCampaignChange } from "./types";
import { validateNumeric } from "./validation";

export function campaignCopyError(
  code: SmartCreationReviewErrorCode,
  field: "sourceCampaignRef" | "productSkus" | "name" | "dailyBudget" | "startingBid",
  message: string,
): never {
  throw new SmartCreationReviewError(422, code, message, { field });
}

export function campaignCopySeed(
  workspace: SmartCreationReviewWorkspace,
  change: AddCampaignChange,
): SmartCreationReviewCampaignSeed {
  const campaign = requireCampaign(workspace.graph, change.sourceCampaignRef);
  if (campaign.excluded.effective) {
    campaignCopyError(
      "smart_creation.review_target_invalid",
      "sourceCampaignRef",
      "An excluded Campaign cannot be copied",
    );
  }
  const draftContent = updateDraftContentFromGraph(
    workspace.draftContent,
    workspace.graph,
  ).campaigns.find((item) => item.itemKey === campaign.ref.itemKey);
  if (!draftContent) throw reviewTargetMissing(change.sourceCampaignRef);
  const groups = workspace.graph.adGroups.filter(
    (group) => group.campaignLineageKey === campaign.ref.lineageKey,
  );
  const groupKeys = new Set(groups.map((group) => group.ref.lineageKey));
  return structuredClone({
    draftContent,
    graph: {
      campaigns: [campaign],
      adGroups: groups,
      targets: workspace.graph.targets.filter((target) => groupKeys.has(target.adGroupLineageKey)),
      negatives: workspace.graph.negatives.filter((negative) =>
        groupKeys.has(negative.adGroupLineageKey),
      ),
    },
  });
}

export function addCampaignCopy(
  workspace: SmartCreationReviewWorkspace,
  change: AddCampaignChange,
  capabilities: CampaignCreationCapabilities,
  seed: SmartCreationReviewCampaignSeed,
): void {
  const campaignKey = `sc:v1:campaign:${hash({ userCampaign: change.id })}`;
  const campaignLineageKey = `review:v1:campaign:user:${change.id}`;
  if (
    workspace.graph.campaigns.some((campaign) => campaign.ref.lineageKey === campaignLineageKey)
  ) {
    campaignCopyError(
      "smart_creation.review_target_invalid",
      "sourceCampaignRef",
      "This Campaign copy already exists",
    );
  }
  if (workspace.graph.campaigns.length >= SMART_CREATION_MAX_CAMPAIGNS) {
    campaignCopyError(
      "smart_creation.capability_unsupported",
      "sourceCampaignRef",
      `A Review can contain at most ${SMART_CREATION_MAX_CAMPAIGNS} Campaigns`,
    );
  }
  if (change.name.length > capabilities.campaignName.maxLength) {
    campaignCopyError(
      "smart_creation.review_name_invalid",
      "name",
      "Campaign name exceeds marketplace limits",
    );
  }
  const sourceCampaign = seed.graph.campaigns[0];
  if (!sourceCampaign) throw new Error("Campaign copy seed is incomplete");
  if (!capabilities.biddingStrategies.includes(sourceCampaign.biddingStrategy.effective)) {
    campaignCopyError(
      "smart_creation.review_bidding_strategy_invalid",
      "sourceCampaignRef",
      "Copied bidding strategy is unavailable in the current marketplace",
    );
  }
  if (
    sourceCampaign.placements.some(
      (placement) =>
        !capabilities.placementAdjustment.placements.includes(placement.placement) ||
        placement.percentage.effective < capabilities.placementAdjustment.min ||
        placement.percentage.effective > capabilities.placementAdjustment.max,
    )
  ) {
    campaignCopyError(
      "smart_creation.review_placement_invalid",
      "sourceCampaignRef",
      "Copied placement is unavailable in the current marketplace",
    );
  }
  try {
    validateNumeric(
      change.dailyBudget,
      capabilities.dailyBudget,
      "smart_creation.review_budget_invalid",
    );
  } catch (error) {
    if (!(error instanceof SmartCreationReviewError)) throw error;
    campaignCopyError("smart_creation.review_budget_invalid", "dailyBudget", error.message);
  }
  if (change.startingBid !== undefined) {
    try {
      validateNumeric(change.startingBid, capabilities.bid, "smart_creation.review_bid_invalid");
    } catch (error) {
      if (!(error instanceof SmartCreationReviewError)) throw error;
      campaignCopyError("smart_creation.review_bid_invalid", "startingBid", error.message);
    }
  }
  const skus = new Set(change.productSkus);
  const sourceProducts = new Map(
    seed.draftContent.adGroups
      .flatMap((group) => group.products)
      .map((product) => [product.sku, product.asin]),
  );
  const currentProducts = new Map(
    workspace.suggestedDraft.campaigns
      .flatMap((campaign) => campaign.adGroups.flatMap((group) => group.products))
      .map((product) => [product.sku, product.asin]),
  );
  if (
    !skus.size ||
    skus.size !== change.productSkus.length ||
    change.productSkus.some(
      (sku) => !sourceProducts.has(sku) || sourceProducts.get(sku) !== currentProducts.get(sku),
    )
  ) {
    campaignCopyError(
      "smart_creation.review_target_invalid",
      "productSkus",
      "Copied products must still belong to the source Campaign and current Plan",
    );
  }
  const selectedGroups = seed.draftContent.adGroups.filter((group) =>
    group.products.some((product) => skus.has(product.sku)),
  );
  const currentGroups = workspace.suggestedDraft.campaigns.flatMap((campaign) => campaign.adGroups);
  for (const group of selectedGroups) {
    const strategyKind = group.targeting.targets[0]?.strategyKind;
    if (
      !currentGroups.some(
        (current) =>
          targetingLane(current.targeting) === targetingLane(group.targeting) &&
          (workspace.draftContent.previewGeneratorVersion < 3 ||
            current.targeting.targets[0]?.strategyKind === strategyKind) &&
          group.products
            .filter((product) => skus.has(product.sku))
            .every((product) =>
              current.products.some(
                (currentProduct) =>
                  currentProduct.sku === product.sku && currentProduct.asin === product.asin,
              ),
            ),
      )
    ) {
      campaignCopyError(
        "smart_creation.review_target_invalid",
        "sourceCampaignRef",
        "The copied Strategy lane is no longer available in the current Plan",
      );
    }
    const reviewedGroup = seed.graph.adGroups.find((item) => item.ref.itemKey === group.itemKey);
    if (!reviewedGroup) throw new Error("Campaign copy Ad Group seed is incomplete");
    validateNumeric(
      change.startingBid ?? reviewedGroup.defaultBid.effective,
      capabilities.bid,
      "smart_creation.review_bid_invalid",
    );
    for (const target of seed.graph.targets.filter(
      (target) =>
        target.adGroupLineageKey === reviewedGroup.ref.lineageKey && !target.excluded.effective,
    )) {
      validateNumeric(
        change.startingBid ?? target.bid.effective,
        capabilities.bid,
        "smart_creation.review_bid_invalid",
      );
      if (
        !strategySupportsReviewIntent(
          target.strategyKind,
          reviewTargetIntent(target),
          workspace.draftContent.previewGeneratorVersion,
        )
      ) {
        campaignCopyError(
          "smart_creation.review_target_invalid",
          "sourceCampaignRef",
          "A copied Target is no longer supported by its Strategy",
        );
      }
    }
  }
  const recipients = workspace.graph.campaigns.filter((campaign) => !campaign.excluded.effective);
  const scale = 10 ** capabilities.dailyBudget.decimals;
  const originalUnits = recipients.map((campaign) =>
    Math.round(campaign.dailyBudget.effective * scale),
  );
  const totalUnits = originalUnits.reduce((total, units) => total + units, 0);
  const remainingUnits = totalUnits - Math.round(change.dailyBudget * scale);
  const minimumUnits = Math.ceil(capabilities.dailyBudget.min * scale);
  const maximumUnits = Math.floor(capabilities.dailyBudget.max * scale);
  const allocatedUnits = allocateBoundedBudgetUnits(
    originalUnits,
    remainingUnits,
    minimumUnits,
    maximumUnits,
  );
  if (!allocatedUnits) {
    campaignCopyError(
      "smart_creation.review_budget_invalid",
      "dailyBudget",
      "The current total budget cannot fund this copy within every existing Campaign minimum and maximum",
    );
  }
  for (const [index, campaign] of recipients.entries()) {
    const amount = (allocatedUnits[index] ?? 0) / scale;
    if (amount < capabilities.dailyBudget.min || amount > capabilities.dailyBudget.max) {
      campaignCopyError(
        "smart_creation.review_budget_invalid",
        "dailyBudget",
        "Budget redistribution would violate an existing Campaign minimum or maximum",
      );
    }
    campaign.dailyBudget = userValue(campaign.dailyBudget, amount);
    campaign.budgetAllocation = userValue(campaign.budgetAllocation, { basis: "amount", amount });
  }
  const copiedCampaign: SmartCreationReviewCampaign = {
    ...structuredClone(sourceCampaign),
    ref: { kind: "campaign", itemKey: campaignKey, lineageKey: campaignLineageKey },
    name: userValue(sourceCampaign.name, change.name),
    dailyBudget: userValue(sourceCampaign.dailyBudget, change.dailyBudget),
    budgetAllocation: userValue(sourceCampaign.budgetAllocation, {
      basis: "amount",
      amount: change.dailyBudget,
    }),
    excluded: userValue(sourceCampaign.excluded, false),
    adGroupLineageKeys: [],
  };
  const copiedGroups: SmartCreationAdGroupDraftContentV2[] = [];
  for (const group of selectedGroups) {
    const reviewedGroup = seed.graph.adGroups.find((item) => item.ref.itemKey === group.itemKey);
    if (!reviewedGroup) throw new Error("Campaign copy Ad Group seed is incomplete");
    const groupKey = `sc:v1:ad-group:${hash({ userCampaign: change.id, sourceGroup: reviewedGroup.ref.lineageKey })}`;
    const groupLineageKey = `review:v1:ad-group:${hash({ userCampaign: change.id, sourceGroup: reviewedGroup.ref.lineageKey })}`;
    const targetKeys = new Map<string, string>();
    for (const original of seed.graph.targets.filter(
      (target) => target.adGroupLineageKey === reviewedGroup.ref.lineageKey,
    )) {
      const lineageKey = `review:v1:target:${hash({ userCampaign: change.id, sourceTarget: original.ref.lineageKey })}`;
      const itemKey = targetStableKey({
        userCampaign: change.id,
        sourceTarget: original.ref.lineageKey,
      });
      targetKeys.set(original.ref.lineageKey, lineageKey);
      workspace.graph.targets.push({
        ...structuredClone(original),
        ref: { kind: "target", itemKey, lineageKey },
        adGroupLineageKey: groupLineageKey,
        ...(change.startingBid === undefined
          ? {}
          : { bid: userValue(original.bid, change.startingBid) }),
      });
    }
    const negativeKeys = new Map<string, string>();
    for (const original of seed.graph.negatives.filter(
      (negative) => negative.adGroupLineageKey === reviewedGroup.ref.lineageKey,
    )) {
      const lineageKey = `review:v1:negative:${hash({ userCampaign: change.id, sourceNegative: original.ref.lineageKey })}`;
      negativeKeys.set(original.ref.lineageKey, lineageKey);
      const copiedNegative = structuredClone(original);
      copiedNegative.ref.lineageKey = lineageKey;
      copiedNegative.adGroupLineageKey = groupLineageKey;
      workspace.graph.negatives.push(copiedNegative);
    }
    const products = group.products.filter((product) => skus.has(product.sku));
    const copiedGroup: SmartCreationReviewAdGroup = {
      ...structuredClone(reviewedGroup),
      ref: { kind: "ad_group", itemKey: groupKey, lineageKey: groupLineageKey },
      campaignLineageKey,
      products,
      ...(change.startingBid === undefined
        ? {}
        : { defaultBid: userValue(reviewedGroup.defaultBid, change.startingBid) }),
      targetLineageKeys: reviewedGroup.targetLineageKeys.map((key) => targetKeys.get(key)!),
      negativeLineageKeys: reviewedGroup.negativeLineageKeys.map((key) => negativeKeys.get(key)!),
    };
    workspace.graph.adGroups.push(copiedGroup);
    copiedCampaign.adGroupLineageKeys.push(groupLineageKey);
    copiedGroups.push({ ...structuredClone(group), itemKey: groupKey, products });
  }
  workspace.graph.campaigns.push(copiedCampaign);
  // A new review initially shares suggested and edited content; manual copies must retain generated provenance.
  workspace.draftContent = structuredClone(workspace.draftContent);
  workspace.draftContent.summary.minimumViableDailyBudget += capabilities.dailyBudget.min;
  workspace.draftContent.campaigns.push({
    ...structuredClone(seed.draftContent),
    itemKey: campaignKey,
    name: change.name,
    dailyBudget: change.dailyBudget,
    startDate: workspace.planSettings.startDate,
    endDate: workspace.planSettings.endDate,
    adGroups: copiedGroups,
  });
  workspace.projections.structure.campaignLineageKeys.push(campaignLineageKey);
  workspace.projections.table.campaignLineageKeys.push(campaignLineageKey);
}
